import { Immutable } from 'nuclear-js'
import { MapOfMapsById, WithId, Post } from './types'
import moment from 'moment'
import camelCase from 'lodash-es/camelCase'
import snakeCase from 'lodash-es/snakeCase'
import startsWith from 'lodash-es/startsWith'
import sortBy from 'lodash-es/sortBy'
import isEmpty from 'lodash-es/isEmpty'
import fromPairs from 'lodash-es/fromPairs'

function toSnakeCase(str: string): string {
  const results = str.match(/([A-z]+)(\d+)$/) // Slider => [], Slider12 => ['Slider', '12']
  if (results && results.length > 0) {
    str = str.substring(0, str.length - results[results.length -1].length)
    str = snakeCase(str)
    str = str + results[results.length - 1]
    return str
  }
  return snakeCase(str)
}

function toCamelCase(thing: any): any {
  if (Array.isArray(thing)) {
    return (<Array<any>>thing).map(toCamelCase)
  } else if (typeof thing === 'object' && thing) {
    const ret = {}
    Object.keys(thing).forEach((k) => {
      ret[camelCase(k)] = toCamelCase(thing[k])
    })
    return ret
  } else {
    return thing
  }
}

function toUnderscore(thing: any): any {
  if (Array.isArray(thing)) {
    return (<Array<any>>thing).map(toUnderscore)
  } else if (typeof thing === 'object' && thing) {
    const ret = {}
    Object.keys(thing).forEach((k) => {
      ret[toSnakeCase(k)] = toUnderscore(thing[k])
    })
    return ret
  } else {
    return thing
  }
}

function toUnderscoreString(str: string): string {
  return str.replace(/([A-Z])/g, m => "_" + m.toLowerCase())
}

function objectById(array: WithId[] = []): Object {
  const ret = {}
  array.forEach((e) => ret[e.id] = e)
  return ret
}

function mapByProp(prop: string, array: any[] = []): Immutable.Map<any, Immutable.Map<string, any>> {
  const tuples = array.map((e) => [e[prop], Immutable.fromJS(e)])
  return Immutable.Map(tuples)
}

// For example: mapping slugs to id's for an array of writers
function mapFromAToB(a: string, b: string, array: any[] = []): Immutable.Map<any, Immutable.Map<string, any>> {
  const tuples = array.map((e) => [e[a], Immutable.fromJS(e[b])])
  return Immutable.Map(tuples)
}

function mapById(array: WithId[] = []): MapOfMapsById {
  return mapByProp('id', array)
}

// for executing something when done, no 'return' value
function donePromise(p: Promise<any>): Promise<void> {
  return new Promise((resolve, reject) => {
    p.then(() => resolve(null))
    p.catch(() => resolve(null))
  })
}

// to check on status of an operation, no 'return' value
function statusPromise(p: Promise<any>): Promise<void> {
  return new Promise((resolve, reject) => {
    p.then(() => resolve(null))
    p.catch(() => reject(null))
  })
}

function daysUntilDate(date: Date): number {
  var today = moment().utc()
  var nextPostDate = moment(date).utc()
  var days = nextPostDate.diff(today, 'days')
  return days
}

function sortedCustomers<C extends { state: string, businessName: string }>(customers: C[]): C[] {
  return sortBy(customers, c => c.state, c => c.businessName?.toLowerCase())
}

function parsedUrl(url: string) {
  const parser = document.createElement('a')
  parser.href = url
  if (!parser.pathname.startsWith('/')) parser.pathname = '/' + parser.pathname
  return parser
}

function prependHttp(url: string) {
  if (!url) return url
  if ( !startsWith(url.toLowerCase(), 'http://') && !startsWith(url.toLowerCase(), 'https://') ) {
    url = 'http://' + url
  }
  return url
}


function isPrimitive(thing: any): boolean {
  return thing === null || thing === undefined || ['number', 'boolean', 'string'].includes(typeof(thing))
}

function prefixString(s: string, prefix: string = null): string {
  if (prefix) {
    return `${prefix}[${s}]`
  } else {
    return s
  }
}

// { a: { b: [1, 2] } } => a[b][]=1&a[b][]=2
function buildQueryString(data: any, prefix: string = null): string {
  const pieces: string[] = []
  for (const k in data) {
    const v = data[k]
    if (v != null) {
      if (Array.isArray(v) && v.length > 0) {
        v.map(x => {
          if (x && isPrimitive(x)) {
            let key = prefixString(k, prefix) + '[]'
            pieces.push(key + '=' + x.toString())
          } else {
            buildQueryString(x, prefixString(k, prefix))
          }
        })
      } else if (isPrimitive(v)) {
        let key = prefixString(k, prefix)
        pieces.push(key + '=' + v)
      } else {
        pieces.push(buildQueryString(v, prefixString(k, prefix)))
      }
    }
  }
  return pieces.join('&')
}

// convert myurl.com?param1=hello&param2=world to {param1: 'hello', param2: 'world'}
function parseQueryString(url: string): any {
  return fromPairs(url.split('?')[1].split('&').map(param => {
    const p = param.split('=')
    return [p[0], decodeURIComponent(p[1])]
  }))
}

function decodeUrlParam(s: string): string {
  if (s) {
    return decodeURIComponent(s.replace(/\+/g, '%20'))
  }
}

function fullDayNoYear(date: Date): string {
  return moment(date).format('dddd, MMMM Do')
}

function shortDayAndMonthNoYear(date: Date): string {
  return moment(date).format('ddd, MMM D')
}

function fullDayWithYear(date: Date): string {
  return moment(date).format('dddd, MMMM Do, YYYY')
}

function fullDayWithTime(date: Date, withTimeZone: boolean = false): string {
  let formattedDate = moment(date).format('dddd, MMMM Do [at] h:mma')
  if(withTimeZone) {
    const zone = moment.tz.guess()
    const timezone = moment.tz(zone).zoneAbbr()
    formattedDate = `${formattedDate} (${timezone})`
  }
  return formattedDate
}

function dayAndMonthNoYear(date: Date): string {
  return moment(date).format('MMM D')
}

function dateRoundOff(date: Date): Date {
  let formattedDate = moment(date).seconds(0).milliseconds(0)
  let extraMinutes = (formattedDate.minutes() % 15)
  formattedDate = moment(date).add(-extraMinutes, "minutes")
  return formattedDate.toDate()
}

function isAgencySignup(account: { agency: boolean, fullySetUp: boolean }): boolean {
  return !!(account && account.agency && !account.fullySetUp)
}

function isSignup(account: { fullySetUp: boolean }): boolean {
  return !account?.fullySetUp
}

function hasSetupAccountProfile(account: { accountProfile: { contactName: string, contactEmail: string } }): boolean {
  return !!(account && account.accountProfile &&
    account.accountProfile.contactName &&
    account.accountProfile.contactEmail)
}

function possessive(str: string): string {
  if (str && str[str.length - 1].toLowerCase() === 's') {
    return str + "'"
  } else {
    return str + "'s"
  }
}

function sortedByLastTransition(posts: Post[], options?: { order: 'asc' | 'ascending' | 'desc' | 'descending' }): Post[] {
  let sorted = sortBy(posts, (p) => new Date(p.lastTransitionAt) )
  if (options && options.order && (options.order.toLowerCase() === 'asc' || options.order.toLowerCase() === 'ascending')) {
    return sorted
  } else {
    return sorted.reverse()
  }
}

// evaluates to true if object has own properties
function present(obj: any): boolean {
  return !isEmpty(obj)
}

function withdrawableDate(post: { sentToCustomerAt: Date }, pretty: boolean = false): any {
  let date = moment(post.sentToCustomerAt).add(6, 'weeks')
  return pretty ? date.format('MMM DD, YYYY') : date
}

function withdrawableSoon(post: { sentToCustomerAt: Date }): boolean {
  return moment().add(2, 'weeks').isSameOrAfter(withdrawableDate(post))
}

function paymentFailedToReactivate(response: any): boolean {
  return response && response.body && response.body.message && !!response.body.message.match('The following error prevented you from starting your subscription: The transaction was declined.')
}

function paramToBool(param: string | number | boolean): boolean {
  if (typeof param === 'string') {
    const lowerCaseParam = param.toLowerCase()
    return ['true', 't', '1'].includes(lowerCaseParam)
  } else {
    return [true, 1].includes(param)
  }
}

function toSentence(array: Array<any>) {
  const wordsConnector = ", ",
      twoWordsConnector = " and ",
      lastWordConnector = ", and "
  let sentence

  switch (array.length) {
    case 0:
      sentence = ""
      break
    case 1:
      sentence = array[0]
      break
    case 2:
      sentence = array[0] + twoWordsConnector + array[1]
      break
    default:
      sentence = array.slice(0, -1).join(wordsConnector) + lastWordConnector + array[array.length - 1]
      break
  }

  return sentence
}

function navigatorLanguage(): string {
  const languageTag = navigator.language
  if (languageTag.match(/[a-z]{2}-[a-z]{2}/i)) {
    const [lang, region] = languageTag.split('-')
    return lang.toLowerCase() + '-' + region.toUpperCase()
  } else {
    return 'en-US'
  }
}

const wait = (ms: number) => {
  return new Promise((resolve, reject) => {
    setTimeout(resolve, ms)
  })
}

function waitUntil<T>(condFn: () => T, intervalMs, limitMs): () => Promise<T> {
  return () => {
    return new Promise<T>((resolve, reject) => {
      let ms = 0
      const i = setInterval(() => {
        if (ms > limitMs) {
          clearInterval(i)
          reject(new Error('Timeout waiting for object'))
        } else {
          const t = condFn()
          if (t) {
            clearInterval(i)
            resolve(t)
          }
        }
        ms += intervalMs
      }, intervalMs)
    })
  }
}

// https://stackoverflow.com/questions/46946380/fetch-api-request-timeout/46946573
// RequestInfo and RequestInit are from the TS fetch definition
function fetchWithTimeout(input: RequestInfo, init: RequestInit, timeout: number) {
  return Promise.race([
    fetch(input, init),
    new Promise((resolve, reject) => {
      setTimeout(() => reject(new Error('timeout')), timeout)
    })
  ])
}

function videoBannerTitle(price: string): string {
  if (price) {
    return `<h5>Add a video for only ${price}... Or subscribe and <span class="highlight">save $10!</span></h5>`
  } else {
    return 'loading...'
  }
}

function hasPlusVideo(customer: { subscription: { planAddOns: {code: string}[] } }): boolean {
  return !!customer?.subscription?.planAddOns?.find(pao => pao.code === 'option-h')
}

function planChangeSummaryPath(config: {customer: {id: number, subscription: { quantity: number, plan: { code: string }, planAddOns: {code: string}[] }}, planCode?: string, options?: string[], quantity?: number}): string {
  const {
    customer,
    planCode,
    options,
    quantity
  } = config
  const code = planCode || customer.subscription.plan.code
  const opts = [options || customer.subscription.planAddOns.map(p => p.code)].join(',')
  const quan = quantity || customer.subscription.quantity

  return `/customers/${customer.id}/subscription/edit?plan_code=${code}&options=${opts}&quantity=${quan}`
}

function videoPrice(customer: { subscription: { extraVideoPrice: number }}): number {
  return customer?.subscription?.extraVideoPrice
}

export function isVideoPlanCode(code: string): boolean {
  //if (!code) return false;
  return [
    'video',
    'annualvideo',
  ].includes(code)
}

export function stripImages(htmlContent: string): string {
  return htmlContent ? htmlContent.replace(/img/gi, 'img4') : ''
}

// For only highlighting outline for .btn-blank class if tabbing through
export function detectMouseOrKeyboard(): void {
  if (document && document.body) {
    document.body.addEventListener('mousedown', () => document.body.classList.add('using-mouse'))
    document.body.addEventListener('keydown', () => document.body.classList.remove('using-mouse'))
  }
}

export const isDate = (str: string): boolean => {
  return new Date(str).toString() !== 'Invalid Date' && !isNaN(new Date(str).getTime())
}

export const quantityChargeDollars = (pieces: number, centsPerPiece: number): string => {
  if (!pieces || !centsPerPiece) {
    return null
  }
  return (centsPerPiece / 100 * pieces).toLocaleString('en-US', { style: 'currency', currency: 'USD'})
}

export {
  toCamelCase,
  toUnderscore,
  toUnderscoreString,
  objectById,
  mapById,
  mapByProp,
  mapFromAToB,
  donePromise,
  daysUntilDate,
  sortedCustomers,
  parsedUrl,
  prependHttp,
  decodeUrlParam,
  fullDayNoYear,
  fullDayWithYear,
  fullDayWithTime,
  dayAndMonthNoYear,
  dateRoundOff,
  shortDayAndMonthNoYear,
  isAgencySignup,
  isSignup,
  hasSetupAccountProfile,
  possessive,
  sortedByLastTransition,
  present,
  buildQueryString,
  withdrawableDate,
  withdrawableSoon,
  paymentFailedToReactivate,
  paramToBool,
  parseQueryString,
  toSentence,
  navigatorLanguage,
  wait,
  waitUntil,
  fetchWithTimeout,
  videoBannerTitle,
  hasPlusVideo,
  planChangeSummaryPath,
  videoPrice
}
